import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Avatar, message, Spin, Form, Select, DatePicker, Space, Row, Col, Modal } from "antd";
import Styled from "styled-components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import upload from "../../../assets/images/upload.svg";
import filtericon from "../../../assets/images/filter-svgrepo-com (1).svg";
import { StyledTable } from "../../../Reusablecomponents/UITable/UITable";
import { ButtonFilled } from "../../../Reusablecomponents/UIButtons";
import { CALL_API } from "../../../Common/api";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { fetchNexusAssets } from "../../TrackRightAI/nexusTrakingApi";
import { withNexusReportShell } from "../NexusReportShell";

// Nexus Trip Allowance Report: same page as the AI Trip Allowance Report, built
// from the allowance saved on each Nexus trip (POST nexus/allowancereport).

dayjs.extend(utc);
dayjs.extend(timezone);

const formatStarted = (v) =>
  v ? dayjs.utc(v).tz("Africa/Nairobi").format("DD/MM/YYYY hh:mm A") : "-";

const { Option } = Select;
const { RangePicker } = DatePicker;

const Customstyle = Styled.div`
  .head { color: #5f6d8c; }
  .Custom { display: flex; align-items: center; gap: 20px; }
  .dropstyle { margin-top: 20px !important; width: 251px !important; }
  .dropstyle1 { height: 36px; width: 251px !important; }
  .badg { padding: 5px; cursor: pointer; }
  .badg .ant-badge-count { font-size: 12px; height: 16px; min-width: 16px; line-height: 16px; }
  .filterstyle { height: 25px; width: 25px; }
  .plc .ant-select-selection-placeholder { color: black !important; }
  .plc .ant-select-arrow { color: black; }
  .header-container { display: flex; justify-content: space-between; align-items: center; width: 100%; }
`;

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const NexusTripAllowanceReport = () => {
  const [form] = Form.useForm();
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.NE_TARPT) && reduxPermissions.NE_TARPT.includes("EX"),
    [reduxPermissions],
  );

  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [formValues, setFormValues] = useState({});
  const [filterCount, setFilterCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chart, setChart] = useState([]);
  const [table, setTable] = useState([]);
  const [columnMissing, setColumnMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [CSVList, setCSVList] = useState([]);
  const [CSVLoader, setCSVLoader] = useState(false);
  const [tableSort, setTableSort] = useState({ field: null, order: null });
  const csvLinkEl = useRef();

  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    fetchNexusAssets()
      .then((r) => setVehicles(r.response || []))
      .catch(() => setVehicles([]));
  }, []);
  const driverNames = useMemo(
    () => Array.from(new Set(vehicles.map((a) => a.driver_name || a.master_driver_name).filter(Boolean))),
    [vehicles],
  );

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const requestBody = useCallback(
    () => ({
      dateFrom: dayjs(selectedDate?.[0]).format("YYYY-MM-DD"),
      dateTo: dayjs(selectedDate?.[1]).format("YYYY-MM-DD"),
      tripType: formValues?.tripType || "",
      vehicle: formValues?.vehicle || "",
      driver: formValues?.driver || "",
    }),
    [selectedDate, formValues],
  );

  const fetchReport = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/allowancereport", requestBody());
      setChart(data?.TripAllowanceChart || []);
      setTable(data?.TripAllowanceTable || []);
      setColumnMissing(Boolean(data?.columnMissing));
    } catch (error) {
      console.error("[NexusTripAllowanceReport] fetch error:", error);
      message.error("Unable to load the trip allowance report.");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, requestBody]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setFilterCount(Object.values(formValues).filter((v) => v && v.length !== 0).length);
  }, [formValues]);

  const sortRows = (rows) => {
    if (!tableSort.field) return rows;
    return [...rows].sort((a, b) => {
      const av = a[tableSort.field];
      const bv = b[tableSort.field];
      const r =
        tableSort.field === "TripAllowance" || tableSort.field === "TripType"
          ? (Number(av) || 0) - (Number(bv) || 0)
          : String(av ?? "").localeCompare(String(bv ?? ""));
      return tableSort.order === "ascend" ? r : -r;
    });
  };

  const col = (title, dataIndex, render) => ({
    title,
    dataIndex,
    key: dataIndex,
    sorter: true,
    sortOrder: tableSort.field === dataIndex ? tableSort.order : null,
    render: render || ((v) => (v === undefined || v === null || v === "" ? "-" : v)),
  });
  const columns = [
    col("Trip Code", "TripCode"),
    col("Trip Type", "TripType", (t) => (t === 1 ? "Local" : t ? "Safari" : "-")),
    col("Vehicle", "VehRegNo"),
    col("Driver Name", "DriverName"),
    col("Started on", "Started", formatStarted),
    col("Trip Allowance", "TripAllowance"),
  ];

  const prepareCSV = async () => {
    try {
      setCSVLoader(true);
      const rows = sortRows(table);
      if (!rows.length) {
        message.error("No data available to download.");
        return;
      }
      setCSVList(
        rows.map((r) => ({
          "Trip Code": r.TripCode || "-",
          "Trip Type": r.TripType === 1 ? "Local" : r.TripType ? "Safari" : "-",
          Vehicle: r.VehRegNo || "-",
          "Driver Name": r.DriverName || "-",
          "Started on": formatStarted(r.Started),
          "Trip Allowance": r.TripAllowance ?? "",
        })),
      );
      setTimeout(() => csvLinkEl.current?.link?.click(), 300);
    } finally {
      setCSVLoader(false);
    }
  };

  return (
    <Customstyle>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <div className="head">Chart View</div>
        <div className="Custom">
          <div className="dropstyle">
            <Form.Item label="" name="dropdown">
              <Select placeholder="Custom Range" value={duration} onChange={setDuration} className="plc">
                {Duration.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="dropstyle1">
            <Space direction="vertical" size={12}>
              <RangePicker value={selectedDate} onChange={(d) => d && setSelectedDate(d)} />
            </Space>
          </div>
          <div>
            <img
              src={upload}
              alt="Upload Icon"
              className="hover-image"
              style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
              onClick={() => {
                if (!exportAllowed) return;
                handleDownloadChart("Export Chart", "Trip Allowance Chart");
              }}
            />
          </div>
          <div>
            <Badge count={filterCount} className="badg" onClick={() => setIsModalOpen(true)}>
              <Avatar shape="circle" size="medium" src={filtericon} className="filterstyle" />
            </Badge>
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        <div style={{ width: "100%" }}>
          <div style={{ width: "calc(100% - 40px)", marginLeft: "20px" }} id="Export Chart">
            {columnMissing ? (
              <p style={{ padding: 24, color: "#5f6d8c" }}>
                Trip allowance isn't being saved yet: the nexusTrip.tripallowance column is missing on this database.
              </p>
            ) : (
              <ResponsiveContainer width="100%" aspect={3}>
                <BarChart
                  data={chart}
                  margin={{ top: 60, right: 30, left: 0, bottom: 40 }}
                  barCategoryGap="30%"
                  barGap={4}
                  style={{ cursor: "pointer" }}
                >
                  <Legend layout="horizontal" align="center" verticalAlign="top" iconType="circle" wrapperStyle={{ top: 10, fontSize: "14px" }} />
                  <CartesianGrid stroke="#E0E0E0" vertical={false} />
                  <XAxis dataKey="RegNo" axisLine={false} tickLine={false} interval={0} tick={{ fontSize: 12, fill: "#5F6D8C" }} />
                  <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => value.toLocaleString()} style={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="TripAllowance" name="Trip Allowance" fill="#E4A400" radius={[5, 5, 0, 0]}>
                    <LabelList
                      dataKey="TripAllowance"
                      content={({ x, y, width, value }) => (
                        <text x={x + width / 2} y={y - 10} fill="#000" fontSize={12} textAnchor="middle">
                          {value}
                        </text>
                      )}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </Spin>
      <br />

      <div className="header-container">
        <div className="head">{table.length} Trip Allowance Report</div>
        <div>
          <Spin spinning={CSVLoader}>
            <img
              style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
              src={upload}
              alt="Upload Icon"
              className="hover-image"
              onClick={() => {
                if (!exportAllowed) return;
                prepareCSV();
              }}
            />
          </Spin>
          <CSVLink data={CSVList} filename={"Nexus Trip Allowance Report.csv"} ref={csvLinkEl} />
        </div>
      </div>
      <br />

      {table.length > 0 && (
        <StyledTable
          dataSource={sortRows(table).map((r) => ({ ...r, key: r.tripid }))}
          columns={columns}
          pagination={false}
          onChange={(pagination, filters, sorter) => setTableSort({ field: sorter.field, order: sorter.order })}
        />
      )}

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={900}
        footer={
          <ButtonFilled
            onClick={() => {
              form.resetFields();
              setFormValues({});
            }}
          >
            Clear All
          </ButtonFilled>
        }
      >
        <h6 className="Filter_for_clickablerow">Filter ({filterCount})</h6>
        <Form form={form} layout="vertical" onValuesChange={(_, all) => setFormValues(all)}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Trip Type" name="tripType">
                <Select className="w-100" allowClear>
                  <Option value={2}>Safari</Option>
                  <Option value={1}>Local</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Vehicle" name="vehicle">
                <Select
                  className="w-100"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={vehicles.map((a) => ({ label: a.asset_name, value: a.asset_id }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Driver" name="driver">
                <Select className="w-100" showSearch allowClear options={driverNames.map((n) => ({ label: n, value: n }))} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Customstyle>
  );
};

export default withNexusReportShell(NexusTripAllowanceReport);
